import { action, computed, makeObservable, observable } from 'mobx';
import { waitFor } from '@/utils/util-functions';
import logger from '@/utils/logger';
import RootStore from '../RootStore';
import {
  Compressor,
  GetAllSubmittedTestsResponseDto,
  TestState,
  ValidationDeviceStatus,
  ValidationStationTestWithDevicesResponseDto,
} from './constants';
import type { StationTestDto } from './constants'
import LogErrorMessage from '@/utils/LogErrorMessage';
import epaxios from '@/utils/epaxios';
import { convertRawTestDataToSattionTestDto } from './InstallationValidationStore.utils';
export default class InstallationValidationStore {
  rootStore: typeof RootStore;
  @observable stationTestData: StationTestDto;
  @observable currentStationId: string;
  @observable isStartingTest = false;
  @observable isEndingTest = false;
  @observable isCancelTest = false;
  @observable isSubmitting = false;
  @observable isStationLoading = false;
  @observable showIsTestAlreadyRunning = false;
  @observable showIsTestAlreadySubmitted = false;
  @observable currentTime: number = Date.now();
  @observable comments: Map<number, string> = new Map();
  @observable timerEndChecking = false;
  @observable timerEndAttempts = 0;
  @observable historyTests: GetAllSubmittedTestsResponseDto[] = [];
  @observable testedBy: string;

  private timerInterval: NodeJS.Timer | null = null;
  private pollingInterval = 30 * 1000;

  @observable error: string | null = null;
  private testPollingInterval: NodeJS.Timer | null = null;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }
  async initTest(currentStationId: string): Promise<void> {
    logger('station validation data refresh with station');
    if (!currentStationId) return this.reset();
    this.isStationLoading = true;
    this.showIsTestAlreadyRunning = false;
    this.showIsTestAlreadySubmitted = false;
    this.currentStationId = currentStationId;
    await this.getHistoryTest();
    await this.getValidationTestResults().finally(() => {
      this.isStationLoading = false;
    });
  }

  private startTimerInterval() {
    this.currentTime = Date.now();
    this.timerInterval = setInterval(() => {
      this.currentTime = Date.now();

      if (this.testState === TestState.RUNNING && this.remainingTimeFormatted === '00:00' && !this.timerEndChecking) {
        this.checkTestAtTimerEnd();
      }
    }, 1000);
  }

  private stopTimerInterval() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  getComment(resultId: number): string {
    return this.comments.get(resultId) || '';
  }
  setShowIsTestAlreadyRunning(value: boolean): void {
    this.showIsTestAlreadyRunning = value;
  }

  setShowIsTestAlreadySubmitted(value: boolean): void {
    this.showIsTestAlreadySubmitted = value;
  }

  updateComment(resultId: number, comment: string) {
    this.comments.set(resultId, comment);
  }

  @computed get remainingTimeFormatted(): string {
    if (!this.stationTestData?.startedAt || this.testState !== TestState.RUNNING) {
      return '60:00';
    }

    const startTime = new Date(this.stationTestData.startedAt).getTime();
    const totalTestDuration = 3600 * 1000;
    const endTime = startTime + totalTestDuration;
    const remainingTime = Math.max(endTime - this.currentTime, 0);
    const remainingSeconds = Math.floor(remainingTime / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  @action getValidationTestResults = async () => {
    try {
      await waitFor(() => this.rootStore.stationStore.isLoading === false);
      const lastTestData = await this.fetchLastTestData();
      this.testedBy = lastTestData.testBy;

      if (lastTestData.isRunning && !lastTestData.isSubmitted) {
        await this.updateLatestTestData();
        this.startTimerInterval();
        this.startTestPolling();
      } else {
        this.stationTestData = convertRawTestDataToSattionTestDto(
          this.rootStore?.stationStore?.currentStation,
          lastTestData,
        );
      }
    } catch (e) {
      logger(`stationTestData error: ${e}`);
      this.error = e instanceof Error ? e.message : 'An error occurred';
    }
  };

  @action cancelTest = async () => {
    try {
      this.isCancelTest = true;
      const stationId = this.rootStore?.stationStore?.currentStation?.id;
      this.stopTestPolling();
      await epaxios.delete(`/onboarding-validations/validation-test-management/station/${stationId}/cancel`);

      await this.updateLastTestData();
      this.stopTimerInterval();
      this.isCancelTest = false;
    } catch (e) {
      if (e.response?.status === 404) {
        await this.updateLastTestData();
      } else {
        LogErrorMessage('Error cancel installation validation test', e);
        this.error = e instanceof Error ? e.message : 'Failed to cancel test';
      }
    } finally {
      this.isCancelTest = false;
    }
  };

  @action startTest = async () => {
    this.isStartingTest = true;
    this.error = null;
    try {
      const stationId = this.rootStore?.stationStore?.currentStation?.id;
      const response = await epaxios.post(
        `/onboarding-validations/validation-test-management/station/${stationId}/start`,
      );
      if (response.data?.status === 409) {
        this.setShowIsTestAlreadyRunning(true);
        return;
      }
      this.comments.clear();
      await this.updateLatestTestData();
      this.startTimerInterval();
      this.startTestPolling();
    } catch (e) {
      LogErrorMessage('Error starting installation validation test', e);
      this.error = e instanceof Error ? e.message : 'Failed to start test';
    } finally {
      this.isStartingTest = false;
    }
  };

  @action continueTest = async () => {
    this.isStartingTest = true;
    this.error = null;
    try {
      const stationId = this.rootStore?.stationStore?.currentStation?.id;
      const response = await epaxios.post(
        `/onboarding-validations/validation-test-management/station/${stationId}/continue`,
      );
      if (response.data?.status === 409) {
        this.setShowIsTestAlreadyRunning(true);
        return;
      }
      this.comments.clear();
      await this.updateLatestTestData();
      this.startTimerInterval();
      this.startTestPolling();
    } catch (e) {
      LogErrorMessage('Error starting installation validation test', e);
      this.error = e instanceof Error ? e.message : 'Failed to start test';
    } finally {
      this.isStartingTest = false;
    }
  };

  @action getHistoryTest = async () => {
    try {
      const stationId = this.rootStore?.stationStore?.currentStation?.id;

      const tests = await epaxios.get(
        `/onboarding-validations/validation-station-test/station/${stationId}/submitted-tests`,
      );
      this.historyTests = tests.data;
    } catch (e) {
      LogErrorMessage('Error ending installation validation test', e);
      this.error = e instanceof Error ? e.message : 'Failed to end test';
    } finally {
      this.isEndingTest = false;
    }
  };

  @action endTest = async () => {
    this.isEndingTest = true;
    this.error = null;
    try {
      const stationId = this.rootStore?.stationStore?.currentStation?.id;
      this.stopTestPolling();

      await epaxios.post(`/onboarding-validations/validation-test-management/station/${stationId}/finish`);

      await this.updateLastTestData();
      this.stopTimerInterval();
      this.isEndingTest = false;
    } catch (e) {
      if (e.response?.status === 404) {
        await this.updateLastTestData();
      } else {
        LogErrorMessage('Error ending installation validation test', e);
        this.error = e instanceof Error ? e.message : 'Failed to end test';
      }
    } finally {
      this.isEndingTest = false;
    }
  };

  @action submitReport = async () => {
    this.isSubmitting = true;
    const stationId = this.rootStore?.stationStore?.currentStation?.id;
    this.error = null;
    try {
      const stationTestId = this.stationTestData.id;
      const submitPayload =
        this.comments.size > 0
          ? Array.from(this.comments.entries()).map(([resultId, comment]) => ({ resultId, comment }))
          : [];

      await epaxios.put(
        `/onboarding-validations/validation-station-test/station/${stationId}/${stationTestId}/submit`,
        submitPayload,
      );
      this.comments.clear();
      await this.getHistoryTest();
      await this.updateLastTestData();
      return true;
    } catch (e) {
      if (e.response?.status === 404) {
        this.setShowIsTestAlreadySubmitted(true);
        return;
      }
      LogErrorMessage('Error submitting validation report', e);
      this.error = e instanceof Error ? e.message : 'Failed to submit report';
      return false;
    } finally {
      this.isSubmitting = false;
    }
  };

  @action private startTestPolling = () => {
    this.stopTestPolling();

    this.testPollingInterval = setInterval(async () => {
      try {
        const data = await this.fetchLastTestData();

        if (!data.isRunning) {
          this.stopTestPolling();
          await this.updateLastTestData();
        } else {
          this.stationTestData = convertRawTestDataToSattionTestDto(this.rootStore?.stationStore?.currentStation, data);
        }
      } catch (e) {
        this.stopTestPolling();
        if (e.response?.status === 404) {
          await this.updateLatestTestData();
        } else {
          LogErrorMessage('Error polling test status', e);
          this.error = e instanceof Error ? e.message : 'Failed to start test';
        }
      }
    }, this.pollingInterval ?? 30000);
  };

  @action private stopTestPolling = () => {
    if (this.testPollingInterval) {
      clearInterval(this.testPollingInterval);
      this.testPollingInterval = null;
    }
  };

  private updateLatestTestData = async () => {
    try {
      const data = await this.fetchLatestTestData();
      this.stationTestData = convertRawTestDataToSattionTestDto(this.rootStore?.stationStore?.currentStation, data);
    } catch (e) {
      logger(`stationTestData error: ${e}`);
    }
  };

  updateLastTestData = async (testId?: string | null) => {
    try {
      const data = testId ? await this.fetchSpecificTestId(testId) : await this.fetchLastTestData();
      this.stationTestData = convertRawTestDataToSattionTestDto(this.rootStore?.stationStore?.currentStation, data);
    } catch (e) {
      this.isStationLoading = false;
      logger(`stationTestData error: ${e}`);
    }
  };

  private fetchSpecificTestId = async (testId: string): Promise<ValidationStationTestWithDevicesResponseDto> => {
    this.isStationLoading = true;
    const stationId = this.rootStore?.stationStore?.currentStation?.id;
    const { data } = await epaxios.get(
      `/onboarding-validations/validation-station-test/station/${stationId}/${testId}`,
    );
    this.testedBy = data.testBy;
    this.isStationLoading = false;
    return data;
  };

  private fetchLastTestData = async (): Promise<ValidationStationTestWithDevicesResponseDto> => {
    const stationId = this.rootStore?.stationStore?.currentStation?.id;
    const { data } = await epaxios.get(
      `/onboarding-validations/validation-station-test/station/${stationId}/last-test`,
    );
    this.testedBy = data.testBy;
    return data;
  };

  private fetchLatestTestData = async (): Promise<ValidationStationTestWithDevicesResponseDto> => {
    const stationId = this.rootStore?.stationStore?.currentStation?.id;
    const { data } = await epaxios.get(`/onboarding-validations/validation-station-test/station/${stationId}`);
    return data;
  };

  @action reset() {
    this.stopTestPolling();
    this.stationTestData = {} as StationTestDto;
    this.currentStationId = undefined;
    this.showIsTestAlreadyRunning = false;
    this.stopTimerInterval();
    this.error = null;
    this.timerEndChecking = false;
    this.timerEndAttempts = 0;
  }

  @computed get unresolvedCompressors(): Compressor[] {
    if (!this.stationTestData?.testDevicesScenario) {
      return [];
    }

    return this.stationTestData.testDevicesScenario.flatMap(scenario =>
      scenario.compressors.filter(
        compressor =>
          compressor.LOAD?.status === ValidationDeviceStatus.FAILED ||
          compressor.LOAD?.status === ValidationDeviceStatus.NOT_TESTED ||
          compressor.UNLOAD?.status === ValidationDeviceStatus.FAILED ||
          compressor.UNLOAD?.status === ValidationDeviceStatus.NOT_TESTED,
      ),
    );
  }

  @computed get resolvedCompressors(): Compressor[] {
    if (!this.stationTestData?.testDevicesScenario) {
      return [];
    }

    return this.stationTestData.testDevicesScenario.flatMap(scenario =>
      scenario.compressors.filter(
        compressor =>
          compressor.LOAD?.status === ValidationDeviceStatus.PASS ||
          compressor.UNLOAD?.status === ValidationDeviceStatus.PASS,
      ),
    );
  }

  @computed get readyForSubmit(): boolean {
    if (!this.stationTestData?.testDevicesScenario) {
      return false;
    }

    const commentsArray = Array.from(this.comments, ([resultId, comment]) => ({
      resultId,
      comment,
    }));

    const hasAllFailedStatusCommented = this.stationTestData.testDevicesScenario.every(scenario =>
      scenario.compressors.every(compressor => {
        const loadFailed =
          compressor.LOAD?.status === ValidationDeviceStatus.FAILED ||
          compressor.LOAD?.status === ValidationDeviceStatus.NOT_TESTED;
        const unloadFailed =
          compressor.UNLOAD?.status === ValidationDeviceStatus.FAILED ||
          compressor.UNLOAD?.status === ValidationDeviceStatus.NOT_TESTED;

        if (!loadFailed && !unloadFailed) {
          return true;
        }
        const hasLoadComment = !loadFailed || commentsArray.some(comment => comment.resultId === compressor.LOAD.id);
        const hasUnloadComment =
          !unloadFailed || commentsArray.some(comment => comment.resultId === compressor.UNLOAD.id);

        return hasLoadComment && hasUnloadComment;
      }),
    );

    return hasAllFailedStatusCommented;
  }

  @action checkTestAtTimerEnd = async () => {
    if (this.timerEndChecking || this.timerEndAttempts >= 3) {
      return;
    }

    this.timerEndChecking = true;
    this.timerEndAttempts = 1;

    const checkAttempt = async () => {
      try {
        this.stopTestPolling();
        logger(`Timer ended - Checking test status (attempt ${this.timerEndAttempts}/3)`);

        const lastTestData = await this.fetchLastTestData();

        if (!lastTestData.isRunning) {
          this.stationTestData = convertRawTestDataToSattionTestDto(
            this.rootStore?.stationStore?.currentStation,
            lastTestData,
          );
          this.timerEndChecking = false;
          return;
        }

        if (this.timerEndAttempts < 3) {
          this.timerEndAttempts++;
          setTimeout(checkAttempt, 1000);
        }
      } catch (e) {
        LogErrorMessage('Error checking test status at timer end', e);
        this.error = e instanceof Error ? e.message : 'Failed to check test status';
      }
    };

    await checkAttempt();
  };

  get testState(): TestState {
    if (!this.stationTestData) {
      return null;
    }

    const { isRunning, isSubmitted, id } = this.stationTestData;

    if ((isRunning === false && isSubmitted === false && id === null) || id === null) {
      return TestState.INITIAL;
    }

    if (isRunning) {
      return TestState.RUNNING;
    }

    if (!isRunning && !isSubmitted) {
      if (this.unresolvedCompressors.length > 0 || this.resolvedCompressors.length === 0) {
        return TestState.NEEDS_REVIEW;
      }
      return TestState.TEST_SUCCESS;
    }

    if (!isRunning && isSubmitted) {
      return TestState.SUBMITTED;
    }
  }
}
